import { ScorerWaitLists } from './ScorerWaitLists';
import type { ScorerWaitList } from '../../lib/scorer-wait-list';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { scrollPinFieldIntoView } from '../common/pin-dialog-frame';
import { ApiError, apiFetch } from '../../lib/api';
import {
  allLanesScored,
  filterShootersByQuery,
  type LaneAdvanceSlot,
} from '../../lib/event-day';
import { SHOT_RANGE_MESSAGE, WHOLE_SHOT_MESSAGE, formatPointsTenths, normalizeBibPrefix } from '../../lib/shooting';
import { strictWholeShotEntry, showShotPoints } from '../../lib/round1';
import { draftTenths, wholeDraftTenths, isCompleteShot, normalizeShotDraft, runningTotal, shotDisplay } from '../../lib/shot-entry';
import type { LeaderboardRow, ScoreRow, ShooterRow, TournamentDetail } from '../../hooks/useScoringTournament';
import { completedScoringTeam, completedShooters, nextTeamShooter, type ScoringTeam } from '../../lib/scoring-navigation';

const fieldClass =
  'w-full rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] px-3 text-[var(--color-text-primary)]';

export interface LaneNow {
  lane: number;
  shooterId: string | null;
  name: string | null;
  bib: string | null;
}

interface ShotSave {
  running: boolean;
  version: number;
  desired?: { value: string; version: number };
  active?: { value: string; version: number };
  error: string | null;
  rejected: boolean;
}

function shotsOf(scores: readonly ScoreRow[], shooterId: string): ScoreRow[] {
  return scores.filter((score) => score.shooterId === shooterId && score.shotNumber > 0);
}

function draftsFromScores(scores: readonly ScoreRow[], shooterId: string, shots: number): string[] {
  const drafts = Array.from({ length: shots }, () => '');
  for (const score of shotsOf(scores, shooterId)) {
    if (score.shotNumber > shots) continue;
    drafts[score.shotNumber - 1] = score.pointsDisplay;
  }
  return drafts;
}

function keepShotVisible(node: HTMLInputElement) {
  const scroll = () => scrollPinFieldIntoView(node);
  scroll();
  window.setTimeout(scroll, 280);
}

/** Fast score entry for the people on lanes 1–6. Saving uses the same per-shot API as the shooter page. */
export function LaneScoreEntry({
  tournamentId,
  bibPrefix,
  shotsPerShooter,
  countedShots,
  lanes,
  lanesReady,
  showLanes,
  shooters,
  scores,
  leaderboardRows,
  onSaved,
  teams = [],
  whole = false,
  scorer,
  onScoresChanged,
}: {
  tournamentId: string;
  bibPrefix?: string;
  shotsPerShooter: number;
  countedShots: number;
  lanes: LaneNow[] | null;
  lanesReady: boolean;
  /** Organizers score by search. The lane list is the admin check-in queue. */
  showLanes: boolean;
  shooters: readonly ShooterRow[];
  scores: readonly ScoreRow[];
  leaderboardRows: readonly LeaderboardRow[];
  onSaved: (patch: { leaderboard?: TournamentDetail['leaderboard']; scores: ScoreRow[] }) => void;
  teams?: readonly ScoringTeam[];
  /** Rifle Round 1: digits 0–10, auto-advance, every shot counts. */
  whole?: boolean;
  scorer?: ScorerWaitList;
  onScoresChanged?: () => void;
}) {
  const prefix = normalizeBibPrefix(bibPrefix);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<string[]>([]);
  const [, redraw] = useState(0);
  const cells = useRef<ShotSave[]>([]);
  const autoAdvance = useRef<string | null>(null);
  const focusAfterSelection = useRef<string | null>(null);
  const historical = useRef(false);
  const selectedRef = useRef<string | null>(null);
  const selectionComplete = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingScorer, setLoadingScorer] = useState(false);
  const loadingScorerRef = useRef(false);
  const entryRef = useRef<HTMLElement | null>(null);
  const scoresRef = useRef(scores);
  const previousScores = useRef(scores);
  if (previousScores.current !== scores) {
    scoresRef.current = scores;
    previousScores.current = scores;
  }
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  const scored = completedShooters(scoresRef.current, shotsPerShooter);
  const slots: LaneAdvanceSlot[] = (lanes ?? []).map((lane) => ({
    lane: lane.lane,
    shooterId: lane.shooterId,
    saved: lane.shooterId ? scored.has(lane.shooterId) : false,
  }));
  const lanesDone = lanesReady && allLanesScored(slots);
  const matches = useMemo(() => filterShootersByQuery(shooters, query, prefix), [shooters, query, prefix]);
  const selected = shooters.find((shooter) => shooter.id === selectedId) ?? null;
  const allCount = countedShots >= shotsPerShooter;
  const live = runningTotal(drafts, countedShots);
  const sumTenths = drafts.reduce((sum, draft) => sum + (draftTenths(draft) ?? 0), 0);

  function changed() { redraw((value) => value + 1); }

  function hasUnsaved() {
    if (!selectedRef.current) return false;
    return cells.current.some((cell, index) => cell.running || cell.desired || cell.error ||
      (whole ? wholeDraftTenths : draftTenths)(draftsRef.current[index] ?? '') !==
      (draftTenths(scoresRef.current.find((score) => score.shooterId === selectedRef.current && score.shotNumber === index + 1)?.pointsDisplay ?? '')) ||
      Boolean(draftsRef.current[index]?.trim() && (whole ? wholeDraftTenths : draftTenths)(draftsRef.current[index]) == null));
  }
  const blocked = hasUnsaved();
  const pending = cells.current.filter((cell) => cell.running || (cell.desired && !cell.error)).length;
  const errors = cells.current.filter((cell) => cell.error).length;
  // Live teammate grids can be newer than this device's cached score rows.
  // Keep the current shooter governed by its own confirmed rows and pending guards.
  const teamCompletion = new Set(scored);
  const members = teams.find((team) => team.teamId && team.members?.some((member) => member.shooterId === selectedId))?.members ?? [];
  for (const row of leaderboardRows) {
    if (row.shooterId === selectedId || !members.some((member) => member.shooterId === row.shooterId) ||
        row.shotsPlanned !== shotsPerShooter || !Array.isArray(row.shots)) continue;
    const complete = completedShooters(row.shots.map((shot) => ({ ...shot, shooterId: row.shooterId })), shotsPerShooter).has(row.shooterId);
    if (complete) teamCompletion.add(row.shooterId);
    else teamCompletion.delete(row.shooterId);
  }
  const doneTeam = whole && selectedId && !blocked && !pending && !loadingScorer
    ? completedScoringTeam(teams, selectedId, teamCompletion) : null;
  const savedCount = selectedId ? shotsOf(scoresRef.current, selectedId).filter((score) => score.shotNumber <= shotsPerShooter).length : 0;

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!hasUnsaved()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  });

  useEffect(() => {
    if (!selectedId || autoAdvance.current !== selectedId || historical.current || blocked || pending || savedCount !== shotsPerShooter) return;
    autoAdvance.current = null;
    if (doneTeam) return;
    const laneIds = new Set((lanes ?? []).flatMap((lane) => lane.shooterId ? [lane.shooterId] : []));
    if (!laneIds.has(selectedId)) return;
    const next = nextTeamShooter(shooters.filter((person) => laneIds.has(person.id)), teams, selectedId, completedShooters(scoresRef.current, shotsPerShooter));
    if (next) selectShooter(next.shooter.id, false, true);
  });

  // Wait for the new teammate's controlled inputs to render before moving focus.
  useEffect(() => {
    if (!selectedId || focusAfterSelection.current !== selectedId || loadingScorer) return;
    const frame = window.requestAnimationFrame(() => {
      if (selectedRef.current !== selectedId || focusAfterSelection.current !== selectedId) return;
      const empty = draftsRef.current.findIndex((draft) => !draft.trim());
      const input = entryRef.current?.querySelector<HTMLInputElement>(`input[data-shot-index="${empty < 0 ? 0 : empty}"]`);
      if (!input || input.disabled) return;
      focusAfterSelection.current = null;
      input.focus();
      input.select();
      keepShotVisible(input);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedId, loadingScorer]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const onResize = () => {
      const active = document.activeElement;
      if (active instanceof HTMLInputElement && active.dataset.shotEntry === 'true') keepShotVisible(active);
    };
    viewport.addEventListener('resize', onResize);
    return () => viewport.removeEventListener('resize', onResize);
  }, [selectedId]);

  function selectShooter(shooterId: string, fromFreshLoad = false, focusAfterRender = false) {
    // openScorer already checked edits before locking the fields for its GET.
    // Fresh server corrections to the previous card must not block that handoff.
    if ((!fromFreshLoad && hasUnsaved()) || loadingScorerRef.current) return;
    focusAfterSelection.current = focusAfterRender ? shooterId : null;
    selectedRef.current = shooterId;
    selectionComplete.current = completedShooters(scoresRef.current, shotsPerShooter).has(shooterId);
    historical.current = false;
    autoAdvance.current = null;
    cells.current = Array.from({ length: shotsPerShooter }, () => ({ running: false, version: 0, error: null, rejected: false }));
    const initial = draftsFromScores(scoresRef.current, shooterId, shotsPerShooter);
    draftsRef.current = initial;
    setSelectedId(shooterId);
    setDrafts(initial);
    setError(null);
    window.setTimeout(() => entryRef.current?.scrollIntoView({ block: 'start' }), 50);
  }

  async function openScorer(shooterId: string) {
    if (hasUnsaved() || loadingScorerRef.current) return;
    loadingScorerRef.current = true;
    setLoadingScorer(true);
    try {
      const fresh = await apiFetch<TournamentDetail>(`/api/admin/shooting/tournaments/${tournamentId}`);
      scoresRef.current = fresh.scores;
      onSaved({ leaderboard: fresh.leaderboard, scores: fresh.scores });
      loadingScorerRef.current = false;
      selectShooter(shooterId, true);
      historical.current = true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the scorecard.');
    } finally {
      loadingScorerRef.current = false;
      setLoadingScorer(false);
    }
  }

  function focusShot(index: number) {
    window.setTimeout(() => {
      document.querySelector<HTMLInputElement>(`input[data-shot-entry="true"][data-shot-index="${index}"]`)?.focus();
    }, 0);
  }

  // Each slot has one worker. A correction or clear replaces its queued value while
  // the current request finishes; different slots never wait on each other.
  async function flush(index: number) {
    const cell = cells.current[index];
    const shooterId = selectedRef.current;
    if (!cell || cell.running || !cell.desired || !shooterId) return;
    cell.running = true;
    changed();
    while (cell.desired) {
      const target = cell.desired;
      cell.active = target;
      cell.desired = undefined;
      const existing = scoresRef.current.find((score) => score.shooterId === shooterId && score.shotNumber === index + 1);
      try {
        let score: ScoreRow | undefined;
        let board: TournamentDetail['leaderboard'] | undefined;
        if (target.value) {
          const body = await apiFetch<{ score: ScoreRow; leaderboard?: TournamentDetail['leaderboard'] }>(
            `/api/admin/shooting/tournaments/${tournamentId}/scores?ack=persisted`,
            { method: 'POST', body: JSON.stringify({ shooterId, shotNumber: index + 1, points: target.value }) },
          );
          score = body.score;
          board = body.leaderboard;
        } else if (existing) {
          const body = await apiFetch<{ leaderboard?: TournamentDetail['leaderboard'] }>(`/api/admin/shooting/scores/${existing.id}?ack=persisted`, { method: 'DELETE' });
          board = body.leaderboard;
        }
        scoresRef.current = scoresRef.current.filter((row) => !(row.shooterId === shooterId && row.shotNumber === index + 1));
        if (score) scoresRef.current = [...scoresRef.current, score];
        onSaved({ ...(board ? { leaderboard: board } : {}), scores: [...scoresRef.current] });
        const complete = completedShooters(scoresRef.current, shotsPerShooter).has(shooterId);
        if (complete !== selectionComplete.current) {
          selectionComplete.current = complete;
          // The regular desk poll handles partial counts. Only a card moving
          // between Waiting and Complete needs an immediate projection refresh.
          onScoresChanged?.();
        }
        if (cell.version === target.version) cell.error = null;
      } catch (err) {
        // A later correction supersedes this failure and can still be attempted.
        if (!cell.desired) {
          if (cell.version === target.version) {
            cell.error = err instanceof ApiError ? err.message : 'Not saved. Check the connection and retry.';
            cell.desired = target;
          } else if (!cell.error) {
            cell.error = 'Previous shot was not saved. Check this value and retry.';
          }
          break;
        }
      }
      changed();
    }
    cell.active = undefined;
    cell.running = false;
    changed();
  }

  function commit(index: number, retry = false) {
    const cell = cells.current[index];
    if (!cell || cell.rejected || (!retry && cell.error && cell.desired && !cell.running)) return;
    const value = draftsRef.current[index]?.trim() ?? '';
    const tenths = (whole ? wholeDraftTenths : draftTenths)(value);
    if (value && tenths == null) {
      cell.error = whole ? WHOLE_SHOT_MESSAGE : SHOT_RANGE_MESSAGE;
      changed();
      return;
    }
    const existing = scoresRef.current.find((score) => score.shooterId === selectedRef.current && score.shotNumber === index + 1);
    cell.error = null;
    if (cell.active?.version === cell.version && (whole ? wholeDraftTenths : draftTenths)(cell.active.value) === tenths && !cell.desired) return;
    if (!cell.running && !cell.desired && tenths === draftTenths(existing?.pointsDisplay ?? '')) {
      changed();
      return;
    }
    cell.desired = { value: tenths == null ? '' : whole ? formatPointsTenths(tenths) : shotDisplay(tenths), version: cell.version };
    if (index + 1 === shotsPerShooter && value) autoAdvance.current = selectedRef.current;
    void flush(index);
  }

  function onDraft(index: number, raw: string) {
    if (loadingScorerRef.current) return;
    const cell = cells.current[index];
    cell.version++;
    cell.desired = undefined;
    let draft: string;
    let advance: boolean;
    if (whole) {
      const entry = strictWholeShotEntry(raw, draftsRef.current[index]);
      if (entry.rejected) {
        cell.rejected = true;
        cell.error = WHOLE_SHOT_MESSAGE;
        changed();
        return;
      }
      draft = entry.draft;
      advance = entry.advance;
    } else {
      draft = normalizeShotDraft(raw);
      advance = isCompleteShot(draft);
    }
    cell.rejected = false;
    cell.error = null;
    const next = draftsRef.current.map((value, i) => i === index ? draft : value);
    draftsRef.current = next;
    setDrafts(next);
    if (!draft || advance) commit(index);
    else if (draft !== '1' && (whole ? wholeDraftTenths : draftTenths)(draft) == null && !draft.endsWith('.')) {
      cell.error = whole ? WHOLE_SHOT_MESSAGE : SHOT_RANGE_MESSAGE;
    }
    if (advance && index + 1 < shotsPerShooter) focusShot(index + 1);
    changed();
  }

  return (
    <section aria-label="Scores" className="space-y-4">
      {showLanes ? (
      <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)]">
        <h2 className="border-b border-[var(--color-border)] px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-[var(--color-text-tertiary)]">
          On the lanes now ({(lanes ?? []).filter(lane => lane.shooterId).length})
        </h2>
        {!lanesReady ? (
          <p className="px-4 py-4 text-sm text-[var(--color-text-secondary)]">Loading the lanes…</p>
        ) : (
          <ol aria-label="Lanes">
            {(lanes ?? []).map((lane) => {
              const saved = lane.shooterId ? scored.has(lane.shooterId) : false;
              const current = lane.shooterId != null && lane.shooterId === selectedId;
              const teamName = shooters.find((person) => person.id === lane.shooterId)?.teamName;
              return (
                <li key={lane.lane} className="border-b border-[var(--color-border)] last:border-b-0">
                  {lane.shooterId ? (
                    <button
                      type="button"
                      disabled={blocked || loadingScorer}
                      onClick={() => selectShooter(lane.shooterId!)}
                      aria-current={current ? 'true' : undefined}
                      className={`flex min-h-16 w-full items-center gap-3 px-4 text-left ${current ? 'bg-[var(--color-accent-subtle)]' : ''}`}
                    >
                      <span className="w-8 shrink-0 text-lg font-semibold tabular-nums text-[var(--color-accent)]">{lane.lane}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-base font-semibold text-[var(--color-text-primary)]">{lane.name}</span>
                        <span className="block truncate text-sm tabular-nums text-[var(--color-text-secondary)]">{lane.bib}{teamName ? ` · ${teamName}` : ''}</span>
                      </span>
                      {saved ? (
                        <span className="text-lg font-semibold text-[var(--color-success-text)]" aria-label="Saved">
                          ✓
                        </span>
                      ) : null}
                    </button>
                  ) : (
                    <div className="flex min-h-14 items-center gap-3 px-4">
                      <span className="w-8 shrink-0 text-lg font-semibold tabular-nums text-[var(--color-text-tertiary)]">{lane.lane}</span>
                      <span className="text-sm text-[var(--color-text-tertiary)]">Open</span>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      ) : null}

      <ScorerWaitLists list={scorer} disabled={blocked || loadingScorer} onSelect={row => { void openScorer(row.shooterId); }} />

      {showLanes && lanesDone ? (
        <p className="rounded-[var(--radius-md)] border border-[var(--color-success)] bg-[var(--color-success-subtle)] px-4 py-3 text-base font-semibold text-[var(--color-success-text)]" role="status">
          All lanes scored.
        </p>
      ) : null}

      <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
        Search by name or number
        <input
          type="search"
          disabled={blocked || loadingScorer}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Name or number, such as 23"
          autoComplete="off"
          className={`${fieldClass} mt-1 h-14 text-lg`}
        />
      </label>
      {query.trim() ? (
        matches.length === 0 ? (
          <p className="text-sm text-[var(--color-text-secondary)]">No shooter matches that search.</p>
        ) : (
          <ul className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)]">
            {matches.map((shooter) => (
              <li key={shooter.id} className="border-b border-[var(--color-border)] last:border-b-0">
                <button type="button" disabled={blocked || loadingScorer} onClick={() => selectShooter(shooter.id)} className="flex min-h-14 w-full items-center justify-between gap-3 px-4 text-left">
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-[var(--color-text-primary)]">{shooter.name}</span>
                    <span className="block text-sm tabular-nums text-[var(--color-text-secondary)]">{shooter.bib ?? 'No number'}{shooter.teamName ? ` · ${shooter.teamName}` : ''}</span>
                  </span>
                  {scored.has(shooter.id) ? (
                    <span className="text-[var(--color-success-text)]" aria-label="Saved">
                      ✓
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}

      {selected ? (
        <section
          ref={entryRef}
          aria-label={`Shots for ${selected.name}`}
          className="scroll-mt-16 space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-1)] p-4"
        >
          <header className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-xl font-semibold text-[var(--color-text-primary)]">{selected.name}</h2>
              <p className="text-sm tabular-nums text-[var(--color-text-secondary)]">{selected.bib ?? 'No number'}{selected.teamName ? ` · ${selected.teamName}` : ''}</p>
            </div>
          </header>
          <div className="sticky top-14 z-20 -mx-4 flex items-end justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-surface-1)] px-4 py-2 md:top-0">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Total{' '}
              <span className="text-2xl font-semibold tabular-nums text-[var(--color-text-primary)]">{showShotPoints(sumTenths, whole)}</span>
            </p>
            <p className="text-right text-sm text-[var(--color-text-secondary)]">
              {allCount ? 'All count' : `Best ${countedShots}`}{' '}
              <span className="text-2xl font-semibold tabular-nums text-[var(--color-text-primary)]">
                {showShotPoints(live.tenths, whole)}
              </span>
            </p>
          </div>
          {whole ? <p className="text-sm text-[var(--color-text-secondary)]">Each shot: 0 to 10 points.</p> : null}
          <div className="grid grid-cols-5 gap-2">
            {drafts.map((draft, index) => {
              const shot = index + 1;
              const dropped = live.dropped.has(shot) && draftTenths(draft) != null;
              return (
                <label key={shot} className="block">
                  <span className="flex items-center justify-between px-0.5 text-xs text-[var(--color-text-tertiary)]">
                    <span>{shot}</span>
                    {dropped ? <span>out</span> : null}
                  </span>
                  <input
                    aria-label={dropped ? `Shot ${shot}, not counted` : `Shot ${shot}`}
                    inputMode={whole ? 'numeric' : 'decimal'}
                    enterKeyHint={shot === drafts.length ? 'done' : 'next'}
                    autoComplete="off"
                    data-shot-entry="true"
                    data-shot-index={index}
                    maxLength={whole ? 16 : 4}
                    placeholder="–"
                    value={draft}
                    disabled={loadingScorer}
                    aria-invalid={Boolean(cells.current[index]?.error)}
                    onBlur={() => commit(index)}
                    onChange={(event) => onDraft(index, event.target.value)}
                    onFocus={(event) => {
                      // Returning to a field means the scorer is reviewing or
                      // correcting this card, even if its last save is pending.
                      autoAdvance.current = null;
                      event.target.select();
                      keepShotVisible(event.target);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') return;
                      event.preventDefault();
                      commit(index, true);
                      if (!cells.current[index]?.error && shot < drafts.length) focusShot(index + 1);
                    }}
                    className={`h-14 w-full scroll-mb-[45vh] scroll-mt-16 rounded-[var(--radius-md)] border-2 bg-[var(--color-surface-1)] px-0 text-center text-lg font-semibold tabular-nums ${
                      dropped
                        ? 'border-dashed border-[var(--color-border-strong)] text-[var(--color-text-tertiary)] line-through'
                        : 'border-[var(--color-border-strong)] text-[var(--color-text-primary)]'
                    }`}
                  />
                  <span className={`block min-h-5 text-center text-xs ${cells.current[index]?.error ? 'text-[var(--color-error-text)]' : 'text-[var(--color-success-text)]'}`} role="status">
                    {cells.current[index]?.error ? 'Error' : cells.current[index]?.running || cells.current[index]?.desired ? 'Saving…' : !cells.current[index]?.rejected && draftTenths(draft) != null && draftTenths(draft) === draftTenths(scoresRef.current.find((score) => score.shooterId === selectedId && score.shotNumber === shot)?.pointsDisplay ?? '') ? 'Saved ✓' : ''}
                  </span>
                  {cells.current[index]?.error ? <span className="block text-xs text-[var(--color-error-text)]" role="alert">{cells.current[index].error}</span> : null}
                  {cells.current[index]?.error && !cells.current[index]?.rejected ? <button type="button" className="min-h-9 text-xs underline" onClick={() => commit(index, true)}>Retry shot {shot}</button> : null}
                </label>
              );
            })}
          </div>
          <p className="text-xs text-[var(--color-text-tertiary)]">
            {whole
              ? `Whole numbers 0–10. ${allCount ? `All ${shotsPerShooter} count.` : `Best ${countedShots} of ${shotsPerShooter} count.`} For 1, press Tab or Enter to move on.`
              : allCount
                ? `All ${shotsPerShooter} shots count. Type a decimal, like 9.7 or 10.4. 0 is a miss.`
                : `Best ${countedShots} of ${shotsPerShooter} count. Type a decimal, like 9.7 or 10.4. 0 is a miss.`}
          </p>
          {error ? (
            <p className="text-sm text-[var(--color-error-text)]" role="alert">
              {error}
            </p>
          ) : null}
          <p className="text-sm text-[var(--color-text-secondary)]" role="status">{savedCount}/{shotsPerShooter} shots saved{pending ? ` · ${pending} saving…` : ''}{errors ? ` · ${errors} shot${errors === 1 ? '' : 's'} need attention` : ''}</p>
          {doneTeam ? (
            <p role="status" className="rounded-[var(--radius-md)] border border-[var(--color-success)] bg-[var(--color-success-subtle)] px-4 py-3 text-base font-semibold text-[var(--color-success-text)]">
              {doneTeam.name} done ✓
            </p>
          ) : null}
        </section>
      ) : (
        <p className="text-sm text-[var(--color-text-secondary)]">Tap a shooter on a lane, or search a name or number.</p>
      )}
    </section>
  );
}
